import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ChefHat, ExternalLink, Plus } from 'lucide-react';
import { useRecipes } from '../hooks/useRecipes';
import { saveSiteContent, savedSiteContent, useSiteContent } from '../hooks/useSiteContent';
import { useAuditLog } from '../hooks/useAuditLog';
import { supabase } from '../lib/supabase';
import { isRichTextBlank } from '../lib/richText';
import { Recipe, RecipeInput, recipeSlug } from '../types/recipe';
import { ImageField } from './SiteImageFields';
import RichTextField from './RichTextField';
import ConfirmationModal from './utils/ConfirmationModal';

interface Props {
  /** executeProtectedAction della dashboard: verifica admin prima di salvare. */
  executeProtectedAction: <T>(action: () => Promise<T>) => Promise<T | null>;
}

const inputClass = 'h-10 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground';
const textareaClass = 'w-full rounded-lg border border-border bg-card px-3 py-2 text-sm leading-6 text-foreground';
const cardClass = 'overflow-hidden rounded-xl bg-card shadow-[0_4px_20px_-2px_rgba(180,83,9,.06)] ring-1 ring-border/40';
const primaryButton =
  'h-10 rounded-lg bg-primary px-4 text-[13px] font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-60';
const ghostButton = 'h-10 rounded-lg px-4 text-[13px] font-semibold text-muted-foreground hover:bg-muted disabled:opacity-60';

/** Bozza del form: i numeri restano stringhe finché non si salva. */
interface Draft {
  title: string;
  slug: string;
  /** Finché è false l'indirizzo segue il titolo; scrivendolo a mano si stacca. */
  slugTouched: boolean;
  summary: string;
  image_url: string | null;
  ingredients: string;
  description: string;
  servings: string;
  prep_minutes: string;
  cook_minutes: string;
  product_id: string;
  published: boolean;
}

const emptyDraft: Draft = {
  title: '',
  slug: '',
  slugTouched: false,
  summary: '',
  image_url: null,
  ingredients: '',
  description: '',
  servings: '',
  prep_minutes: '',
  cook_minutes: '',
  product_id: '',
  published: false,
};

const toDraft = (r: Recipe): Draft => ({
  title: r.title,
  slug: r.slug,
  slugTouched: true,
  summary: r.summary,
  image_url: r.image_url,
  ingredients: r.ingredients.join('\n'),
  description: r.description,
  servings: r.servings?.toString() ?? '',
  prep_minutes: r.prep_minutes?.toString() ?? '',
  cook_minutes: r.cook_minutes?.toString() ?? '',
  product_id: r.product_id ?? '',
  published: r.published,
});

/** "" -> null; numero intero >= min -> numero; altro -> NaN */
const parseCount = (value: string, min: number): number | null => {
  const v = value.trim();
  if (!v) return null;
  return /^\d+$/.test(v) && Number(v) >= min && Number(v) < 32768 ? Number(v) : NaN;
};

/** Dati da salvare, oppure il primo problema da mostrare. */
function toInput(d: Draft): RecipeInput | string {
  if (!d.title.trim()) return 'Dai un titolo alla ricetta';
  const slug = recipeSlug(d.slug || d.title);
  if (!slug) return "L'indirizzo della pagina deve contenere lettere o numeri";
  const ingredients = d.ingredients
    .split('\n')
    .map((line) => line.replace(/^[\s\-•*]+/, '').trim())
    .filter(Boolean);
  const servings = parseCount(d.servings, 1);
  const prep = parseCount(d.prep_minutes, 0);
  const cook = parseCount(d.cook_minutes, 0);
  if (Number.isNaN(servings)) return 'Porzioni: scrivi un numero intero, es. 4';
  if (Number.isNaN(prep) || Number.isNaN(cook)) return 'I tempi vanno in minuti, es. 45';
  if (d.published) {
    if (ingredients.length === 0) return 'Per pubblicare la ricetta aggiungi almeno un ingrediente';
    if (isRichTextBlank(d.description)) return 'Per pubblicare la ricetta scrivi la preparazione';
  }
  return {
    title: d.title.trim(),
    slug,
    summary: d.summary.trim(),
    image_url: d.image_url,
    ingredients,
    description: isRichTextBlank(d.description) ? '' : d.description,
    servings,
    prep_minutes: prep,
    cook_minutes: cook,
    product_id: d.product_id || null,
    published: d.published,
  };
}

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-xs font-semibold text-muted-foreground">{label}</span>
    {children}
    {hint && <span className="text-xs leading-5 text-muted-foreground">{hint}</span>}
  </label>
);

/** Titolo e introduzione della pagina /ricette (site_content, riga page.recipes). */
const RecipesPageSettings: React.FC<Props> = ({ executeProtectedAction }) => {
  const { content } = useSiteContent('page.recipes');
  const { logUpdateAction } = useAuditLog();
  const [draft, setDraft] = useState(content);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(content), [content]);
  const dirty = draft.title !== content.title || draft.intro !== content.intro;

  const save = async () => {
    const value = { title: draft.title.trim(), intro: draft.intro.trim() };
    if (!value.title || !value.intro) {
      toast.error('Titolo e introduzione non possono essere vuoti');
      return;
    }
    setSaving(true);
    await executeProtectedAction(async () => {
      try {
        const before = savedSiteContent('page.recipes');
        await saveSiteContent('page.recipes', value);
        await logUpdateAction('site_content', 'page.recipes', { content: before }, { content: value });
        toast.success('Pagina ricette salvata');
        return true;
      } catch (err: any) {
        toast.error(err.message);
        return false;
      }
    });
    setSaving(false);
  };

  return (
    <section className={cardClass}>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-6 py-5">
        <div>
          <h2 className="font-display text-xl font-semibold text-foreground">Pagina ricette</h2>
          <p className="mt-1 text-sm leading-[22px] text-muted-foreground">Il titolo e il testo sopra l'elenco delle ricette.</p>
        </div>
        <a
          href="/ricette"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-[13px] font-semibold text-primary hover:underline"
        >
          Vedi sul sito
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      <div className="flex flex-col gap-4 px-6 py-5">
        <Field label="Titolo">
          <input className={inputClass} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
        </Field>
        <Field label="Introduzione">
          <textarea rows={2} className={textareaClass} value={draft.intro} onChange={(e) => setDraft({ ...draft, intro: e.target.value })} />
        </Field>
        <button type="button" onClick={save} disabled={!dirty || saving} className={`${primaryButton} self-end`}>
          {saving ? 'Salvo…' : 'Salva'}
        </button>
      </div>
    </section>
  );
};

/** Vista Contenuti → Ricette: elenco, creazione, modifica ed eliminazione. */
const RecipesEditor: React.FC<Props> = ({ executeProtectedAction }) => {
  const { recipes, loading, error, saveRecipe, deleteRecipe } = useRecipes();
  const { logCreateAction, logUpdateAction, logDeleteAction } = useAuditLog();
  const [products, setProducts] = useState<{ id: string; name: string; active: boolean }[]>([]);
  // null = elenco; 'new' = nuova ricetta; altrimenti l'id della ricetta aperta.
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    supabase
      .from('products')
      .select('id, name, active')
      .order('name')
      .then(({ data }) => setProducts(data ?? []));
  }, []);

  const current = editing && editing !== 'new' ? recipes.find((r) => r.id === editing) ?? null : null;
  const dirty = editing !== null && JSON.stringify(draft) !== JSON.stringify(current ? toDraft(current) : emptyDraft);

  // Chi chiude la scheda con modifiche non salvate riceve l'avviso del browser.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const open = (recipe: Recipe | null) => {
    setDraft(recipe ? toDraft(recipe) : emptyDraft);
    setEditing(recipe ? recipe.id : 'new');
  };

  const close = () => {
    if (dirty && !window.confirm('Hai modifiche non salvate. Chiudere comunque?')) return;
    setEditing(null);
  };

  const set = (changes: Partial<Draft>) =>
    setDraft((prev) => {
      const next = { ...prev, ...changes };
      if ('title' in changes && !prev.slugTouched) next.slug = recipeSlug(changes.title ?? '');
      return next;
    });

  const save = async () => {
    const input = toInput(draft);
    if (typeof input === 'string') {
      toast.error(input);
      return;
    }
    setSaving(true);
    await executeProtectedAction(async () => {
      try {
        const saved = await saveRecipe(current?.id ?? null, input);
        if (current) await logUpdateAction('recipes', saved.id, current, input);
        else await logCreateAction('recipes', saved.id, input);
        setDraft(toDraft(saved));
        setEditing(saved.id);
        toast.success(saved.published ? 'Ricetta salvata e pubblicata' : 'Bozza salvata: non è ancora visibile sul sito');
        return true;
      } catch (err: any) {
        toast.error(err.message);
        return false;
      }
    });
    setSaving(false);
  };

  const remove = async () => {
    setConfirmDelete(false);
    if (!current) return;
    setSaving(true);
    await executeProtectedAction(async () => {
      try {
        await deleteRecipe(current.id);
        await logDeleteAction('recipes', current.id, current);
        setEditing(null);
        toast.success('Ricetta eliminata');
        return true;
      } catch (err: any) {
        toast.error(err.message);
        return false;
      }
    });
    setSaving(false);
  };

  if (editing === null) {
    return (
      <div className="flex flex-col gap-6">
        <RecipesPageSettings executeProtectedAction={executeProtectedAction} />

        <section className={cardClass}>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-5">
            <div>
              <h2 className="font-display text-xl font-semibold text-foreground">Ricette</h2>
              <p className="mt-1 text-sm leading-[22px] text-muted-foreground">
                Le bozze le vedi solo tu: sul sito compaiono quando le pubblichi.
              </p>
            </div>
            <button type="button" onClick={() => open(null)} className={`${primaryButton} flex items-center gap-1.5`}>
              <Plus className="h-4 w-4" />
              Nuova ricetta
            </button>
          </div>

          {loading && <div className="px-6 py-5 text-sm text-muted-foreground">Caricamento…</div>}
          {error && <div className="px-6 py-5 text-sm text-danger">{error}</div>}
          {!loading && !error && recipes.length === 0 && (
            <div className="px-6 py-10 text-center">
              <ChefHat className="mx-auto mb-3 h-8 w-8 text-primary" />
              <p className="font-semibold text-foreground">Scrivi la prima ricetta</p>
              <p className="mt-1 text-sm text-muted-foreground">Ingredienti, preparazione e una bella foto: il resto lo fa il sito.</p>
            </div>
          )}

          {recipes.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => open(r)}
              className="flex w-full items-center gap-4 border-b border-border px-6 py-3 text-left last:border-b-0 hover:bg-muted/40"
            >
              {r.image_url ? (
                <img src={r.image_url} alt="" className="h-12 w-16 flex-none rounded-md object-cover" />
              ) : (
                <div className="flex h-12 w-16 flex-none items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <ChefHat className="h-5 w-5" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-foreground">{r.title}</div>
                <div className="truncate text-xs text-muted-foreground">/ricette/{r.slug}</div>
              </div>
              <span
                className="flex-none rounded-full px-3 py-1 text-[11px] font-bold tracking-wide"
                style={
                  r.published
                    ? { background: 'hsl(var(--eco))', color: 'hsl(var(--eco-foreground))' }
                    : { background: 'hsl(var(--secondary))', color: 'hsl(var(--muted-foreground))' }
                }
              >
                {r.published ? 'PUBBLICATA' : 'BOZZA'}
              </span>
            </button>
          ))}
        </section>
      </div>
    );
  }

  return (
    <section className={cardClass}>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-6 py-5">
        <div className="min-w-0">
          <button type="button" onClick={close} className="mb-2 text-[13px] font-semibold text-primary hover:underline">
            ← Tutte le ricette
          </button>
          <h2 className="font-display text-xl font-semibold text-foreground">{current ? current.title : 'Nuova ricetta'}</h2>
        </div>
        {current?.published && (
          <a
            href={`/ricette/${current.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-[13px] font-semibold text-primary hover:underline"
          >
            Vedi sul sito
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>

      <div className="flex flex-col gap-5 px-6 py-5">
        <Field label="Titolo">
          <input className={inputClass} value={draft.title} placeholder="Yogurt, noci e miele millefiori" onChange={(e) => set({ title: e.target.value })} />
        </Field>
        <Field label="Indirizzo della pagina" hint="Si compila dal titolo. Se lo cambi dopo la pubblicazione, il vecchio link smette di funzionare.">
          <div className="flex items-center overflow-hidden rounded-lg border border-border bg-card">
            <span className="flex h-10 items-center bg-muted px-3 text-sm text-muted-foreground">/ricette/</span>
            <input
              className="h-10 min-w-0 flex-1 bg-transparent px-3 text-sm text-foreground focus:outline-none"
              value={draft.slug}
              onChange={(e) => set({ slug: e.target.value.toLowerCase(), slugTouched: true })}
              onBlur={() => set({ slug: recipeSlug(draft.slug) })}
            />
          </div>
        </Field>
        <Field label="Presentazione" hint="Una o due frasi: compaiono nella scheda della ricetta e su Google.">
          <textarea rows={2} className={textareaClass} value={draft.summary} onChange={(e) => set({ summary: e.target.value })} />
        </Field>
        <ImageField
          label="Foto"
          value={draft.image_url}
          prefix={`recipe-${draft.slug || 'nuova'}`}
          hint="Orizzontale, almeno 1200 px di larghezza, massimo 5 MB."
          onChange={(image_url) => set({ image_url })}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Porzioni">
            <input inputMode="numeric" className={inputClass} value={draft.servings} placeholder="4" onChange={(e) => set({ servings: e.target.value })} />
          </Field>
          <Field label="Preparazione (minuti)">
            <input inputMode="numeric" className={inputClass} value={draft.prep_minutes} placeholder="15" onChange={(e) => set({ prep_minutes: e.target.value })} />
          </Field>
          <Field label="Cottura (minuti)">
            <input inputMode="numeric" className={inputClass} value={draft.cook_minutes} placeholder="vuoto se non si cuoce" onChange={(e) => set({ cook_minutes: e.target.value })} />
          </Field>
        </div>
        <Field label="Ingredienti" hint="Uno per riga, con la quantità.">
          <textarea
            rows={7}
            className={textareaClass}
            value={draft.ingredients}
            placeholder={'250 g di yogurt greco\n2 cucchiai di miele millefiori\n1 manciata di noci'}
            onChange={(e) => set({ ingredients: e.target.value })}
          />
        </Field>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-muted-foreground">Preparazione</span>
          <RichTextField label="Preparazione della ricetta" value={draft.description} onChange={(description) => set({ description })} />
          <span className="text-xs leading-5 text-muted-foreground">Un elenco numerato è comodo per i passaggi.</span>
        </div>
        <Field label="Miele consigliato" hint="Nella ricetta compare il link al prodotto, se è in vendita.">
          <select className={inputClass} value={draft.product_id} onChange={(e) => set({ product_id: e.target.value })}>
            <option value="">Nessuno</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.active ? '' : ' (nascosto)'}
              </option>
            ))}
          </select>
        </Field>
        <label className="flex items-center gap-3 rounded-lg bg-muted/50 px-4 py-3">
          <input type="checkbox" className="h-4 w-4 accent-primary" checked={draft.published} onChange={(e) => set({ published: e.target.checked })} />
          <span className="text-sm">
            <span className="font-semibold text-foreground">Pubblicata sul sito</span>
            <span className="block text-xs text-muted-foreground">Togli la spunta per tenerla in bozza.</span>
          </span>
        </label>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-muted/30 px-6 py-4">
        {current ? (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            disabled={saving}
            className="h-10 rounded-lg px-3 text-[13px] font-semibold text-danger hover:bg-danger/10"
          >
            Elimina ricetta
          </button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <button type="button" onClick={close} disabled={saving} className={ghostButton}>
            {dirty ? 'Annulla' : 'Chiudi'}
          </button>
          <button type="button" onClick={save} disabled={saving || (!dirty && !!current)} className={primaryButton}>
            {saving ? 'Salvo…' : draft.published ? 'Salva e pubblica' : 'Salva bozza'}
          </button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        title={`Eliminare "${current?.title}"?`}
        message="La ricetta sparisce dal sito e non si può recuperare. Se vuoi solo nasconderla, togli la spunta Pubblicata."
        confirmText="Elimina"
        type="danger"
      />
    </section>
  );
};

export default RecipesEditor;
